import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Marquee } from "@/components/ui/3d-testimonials";

/** When true, children should render lightweight shells (no video/network). */
export const ScrollStripMeasureContext = createContext(false);

export function useScrollStripMeasure() {
  return useContext(ScrollStripMeasureContext);
}

/**
 * Horizontal strip: static centered when content fits, marquee when it overflows.
 * duration=0 disables auto-scroll (always static).
 */
export function ScrollStrip({
  duration = 20,
  pauseOnHover = true,
  gap = "1rem",
  className,
  children,
}) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  const childArray = React.Children.toArray(children);

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const check = () => {
      setNeedsScroll(measure.scrollWidth > container.clientWidth + 4);
    };

    check();
    const ro = new ResizeObserver(check);
    ro.observe(container);
    ro.observe(measure);
    return () => ro.disconnect();
  }, [childArray.length]);

  if (childArray.length === 0) return null;

  const animate = needsScroll && duration > 0;

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full min-w-0 max-w-full overflow-hidden", className)}
    >
      {/* Off-screen measure — shells only, no media playback */}
      <ScrollStripMeasureContext.Provider value={true}>
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none fixed left-[-10000px] top-0 flex w-max opacity-0"
          style={{ gap }}
        >
          {childArray}
        </div>
      </ScrollStripMeasureContext.Provider>

      {animate ? (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-[#faf8f8] to-transparent sm:w-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-[#faf8f8] to-transparent sm:w-10" />
          <Marquee
            pauseOnHover={pauseOnHover}
            repeat={2}
            className="py-1 [--gap:var(--strip-gap)]"
            style={{ "--duration": `${duration}s`, "--strip-gap": gap }}
          >
            {childArray}
          </Marquee>
        </>
      ) : (
        <div
          data-lenis-prevent
          className={cn(
            "flex flex-wrap items-center justify-center",
            needsScroll &&
              "touch-pan-x justify-start overflow-x-auto overscroll-x-contain pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          )}
          style={{ gap, scrollSnapType: needsScroll ? "x mandatory" : undefined }}
        >
          {childArray}
        </div>
      )}
    </div>
  );
}

export default ScrollStrip;
